import { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import Markdown from '@/components/legal/Markdown'
import {
    maintenanceService,
    type ComponentStatus,
    type MaintenanceComponent,
    type MaintenanceOverview,
    type Procedure,
    type RoadmapItem,
    type RoadmapPriority,
    type RoadmapStatus,
} from '@/services/maintenanceService'

/** Semáforo: etiqueta y colores de cada estado. */
export const STATUS_META: Record<ComponentStatus, { label: string; badge: string; dot: string }> = {
    EOL: { label: 'Sin soporte', badge: 'bg-rose-500/15 text-rose-300 border-rose-500/40', dot: 'bg-rose-400' },
    EOL_SOON: { label: 'Soporte por vencer', badge: 'bg-orange-500/15 text-orange-300 border-orange-500/40', dot: 'bg-orange-400' },
    MAJOR: { label: 'Línea nueva', badge: 'bg-amber-500/15 text-amber-300 border-amber-500/40', dot: 'bg-amber-400' },
    UPDATE: { label: 'Actualización', badge: 'bg-yellow-500/15 text-yellow-200 border-yellow-500/40', dot: 'bg-yellow-300' },
    OK: { label: 'Al día', badge: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40', dot: 'bg-emerald-400' },
    UNKNOWN: { label: 'Sin datos', badge: 'bg-slate-500/15 text-slate-300 border-slate-500/40', dot: 'bg-slate-400' },
    ERROR: { label: 'Error al consultar', badge: 'bg-slate-500/15 text-slate-300 border-slate-500/40', dot: 'bg-slate-400' },
}

const SUMMARY: { key: string; label: string; states: ComponentStatus[]; color: string }[] = [
    { key: 'eol', label: 'Sin soporte', states: ['EOL'], color: 'text-rose-300' },
    { key: 'soon', label: 'Soporte por vencer', states: ['EOL_SOON'], color: 'text-orange-300' },
    { key: 'new', label: 'Versiones nuevas', states: ['MAJOR', 'UPDATE'], color: 'text-amber-300' },
    { key: 'ok', label: 'Al día', states: ['OK'], color: 'text-emerald-300' },
    { key: 'unknown', label: 'Sin datos', states: ['UNKNOWN', 'ERROR'], color: 'text-slate-300' },
]

const PRIORITY_BADGE: Record<RoadmapPriority, string> = {
    ALTA: 'bg-rose-500/15 text-rose-300',
    MEDIA: 'bg-amber-500/15 text-amber-300',
    BAJA: 'bg-slate-500/20 text-slate-300',
}
const ROADMAP_STATUS: Record<RoadmapStatus, string> = {
    PENDIENTE: 'Pendiente',
    EN_CURSO: 'En curso',
    HECHO: 'Hecho',
    DESCARTADO: 'Descartado',
}

const SEVERITY: ComponentStatus[] = ['EOL', 'EOL_SOON', 'MAJOR', 'UPDATE', 'ERROR', 'UNKNOWN', 'OK']

function fmtDate(value: string | null | undefined, withTime = false): string {
    if (!value) return '—'
    const d = new Date(value.length === 10 ? `${value}T12:00:00` : value)
    if (Number.isNaN(d.getTime())) return value
    return withTime
        ? d.toLocaleString('es-CL', { dateStyle: 'medium', timeStyle: 'short' })
        : d.toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' })
}

function errorMessage(e: unknown, fallback: string): string {
    const err = e as { response?: { data?: { error?: string } } }
    return err?.response?.data?.error ?? fallback
}

const inputCls =
    'rounded-md border border-slate-600 bg-slate-900 px-3 py-2 text-sm text-slate-100 outline-none focus:border-indigo-500'

type Tab = 'components' | 'roadmap' | 'procedures'

export default function PlatformMaintenancePage() {
    const [tab, setTab] = useState<Tab>('components')
    const [overview, setOverview] = useState<MaintenanceOverview | null>(null)
    const [roadmap, setRoadmap] = useState<RoadmapItem[]>([])
    const [showClosed, setShowClosed] = useState(false)
    const [filter, setFilter] = useState<string | null>(null)
    const [procedure, setProcedure] = useState<Procedure | null>(null)
    const [loading, setLoading] = useState(true)

    const loadOverview = useCallback(async () => {
        try {
            setOverview(await maintenanceService.overview())
        } catch {
            toast.error('No se pudo cargar el estado de mantenimiento')
        } finally {
            setLoading(false)
        }
    }, [])

    const loadRoadmap = useCallback(async () => {
        try {
            setRoadmap(await maintenanceService.roadmap(showClosed))
        } catch {
            toast.error('No se pudo cargar la hoja de ruta')
        }
    }, [showClosed])

    useEffect(() => { loadOverview() }, [loadOverview])
    useEffect(() => { loadRoadmap() }, [loadRoadmap])

    // Mientras hay una revisión en curso, refrescar cada 3 s.
    const running = overview?.running ?? false
    useEffect(() => {
        if (!running) return
        const t = setInterval(async () => {
            const o = await maintenanceService.overview().catch(() => null)
            if (!o) return
            setOverview(o)
            if (!o.running) {
                toast.success('Revisión terminada')
                loadRoadmap()
            }
        }, 3000)
        return () => clearInterval(t)
    }, [running, loadRoadmap])

    const startCheck = async () => {
        try {
            await maintenanceService.startCheck()
            setOverview((o) => (o ? { ...o, running: true } : o))
            toast.info('Revisando versiones contra las fuentes oficiales…')
        } catch (e) {
            toast.error(errorMessage(e, 'No se pudo iniciar la revisión'))
        }
    }

    const openProcedure = async (id: string | null) => {
        if (!id) return
        try {
            setProcedure(await maintenanceService.procedure(id))
        } catch {
            toast.error('No se pudo cargar el procedimiento')
        }
    }

    const components = overview?.components ?? []
    const counts = useMemo(() => {
        const c: Record<string, number> = {}
        for (const s of SUMMARY) c[s.key] = components.filter((x) => s.states.includes(x.status)).length
        return c
    }, [components])
    const openItems = roadmap.filter((r) => r.status === 'PENDIENTE' || r.status === 'EN_CURSO').length

    return (
        <div>
            <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-semibold">Mantenimiento técnico</h1>
                    <p className="mt-1 text-sm text-slate-400">
                        Versiones de cada componente de VANTAK contrastadas con sus fuentes oficiales. Se revisa sola cada mes y
                        avisa si algo pierde soporte.
                    </p>
                    <p className="mt-2 text-xs text-slate-500">
                        {overview?.lastRun
                            ? <>Última revisión: {fmtDate(overview.lastRun.finishedAt ?? overview.lastRun.startedAt, true)} ·{' '}
                                {overview.lastRun.trigger === 'SCHEDULED' ? 'automática' : `manual (${overview.lastRun.triggeredBy ?? '—'})`}</>
                            : 'Aún no hay revisiones.'}
                        {' · '}Inventario del servidor: {overview?.hostInventoryAt ? fmtDate(overview.hostInventoryAt, true) : 'no disponible'}
                    </p>
                </div>
                <button
                    onClick={startCheck}
                    disabled={running}
                    className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500 disabled:cursor-wait disabled:opacity-60"
                >
                    {running ? 'Revisando…' : 'Revisar ahora'}
                </button>
            </div>

            <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-5">
                {SUMMARY.map((s) => (
                    <button
                        key={s.key}
                        onClick={() => { setTab('components'); setFilter(filter === s.key ? null : s.key) }}
                        className={`rounded-xl border p-3 text-left transition-colors ${filter === s.key ? 'border-indigo-500 bg-slate-800' : 'border-slate-800 bg-slate-800/50 hover:bg-slate-800'}`}
                    >
                        <div className={`text-2xl font-semibold ${s.color}`}>{counts[s.key] ?? 0}</div>
                        <div className="text-xs text-slate-400">{s.label}</div>
                    </button>
                ))}
            </div>

            <div className="mb-4 flex gap-1 border-b border-slate-800" role="tablist">
                {([['components', 'Componentes'], ['roadmap', `Hoja de ruta (${openItems})`], ['procedures', 'Procedimientos']] as [Tab, string][]).map(([t, label]) => (
                    <button
                        key={t}
                        role="tab"
                        aria-selected={tab === t}
                        onClick={() => setTab(t)}
                        className={`-mb-px border-b-2 px-4 py-2 text-sm ${tab === t ? 'border-indigo-500 text-slate-100' : 'border-transparent text-slate-400 hover:text-slate-200'}`}
                    >
                        {label}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="text-slate-400">Cargando…</div>
            ) : tab === 'components' ? (
                <ComponentsTab
                    components={components}
                    filter={SUMMARY.find((s) => s.key === filter)?.states ?? null}
                    onClearFilter={() => setFilter(null)}
                    onProcedure={openProcedure}
                />
            ) : tab === 'roadmap' ? (
                <RoadmapTab
                    items={roadmap}
                    components={components}
                    showClosed={showClosed}
                    onToggleClosed={() => setShowClosed((v) => !v)}
                    onChanged={loadRoadmap}
                />
            ) : (
                <ProceduresTab onOpen={openProcedure} />
            )}

            {procedure && (
                <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-4" onClick={() => setProcedure(null)}>
                    <div
                        role="dialog"
                        aria-label={procedure.title}
                        className="my-8 w-full max-w-3xl rounded-xl border border-slate-700 bg-slate-900 p-6"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="mb-2 flex justify-end">
                            <button onClick={() => setProcedure(null)} className="rounded-md border border-slate-700 px-3 py-1 text-sm text-slate-300 hover:bg-slate-800">
                                Cerrar
                            </button>
                        </div>
                        <Markdown content={procedure.content ?? ''} />
                    </div>
                </div>
            )}
        </div>
    )
}

function StatusBadge({ status }: { status: ComponentStatus }) {
    const m = STATUS_META[status]
    return (
        <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2 py-0.5 text-xs ${m.badge}`}>
            <span className={`h-2 w-2 rounded-full ${m.dot}`} />
            {m.label}
        </span>
    )
}

function ComponentsTab({ components, filter, onClearFilter, onProcedure }: {
    components: MaintenanceComponent[]
    filter: ComponentStatus[] | null
    onClearFilter: () => void
    onProcedure: (id: string | null) => void
}) {
    const visible = filter ? components.filter((c) => filter.includes(c.status)) : components
    const categories = Array.from(new Set(visible.map((c) => c.category)))

    if (visible.length === 0) {
        return (
            <div className="text-sm text-slate-400">
                No hay componentes en este estado.{' '}
                <button onClick={onClearFilter} className="text-indigo-400 underline">Ver todos</button>
            </div>
        )
    }

    return (
        <div className="space-y-6">
            {categories.map((cat) => (
                <section key={cat}>
                    <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-400">{cat}</h2>
                    <div className="overflow-x-auto rounded-xl border border-slate-800">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-800/60 text-left text-xs text-slate-400">
                                <tr>
                                    <th className="px-3 py-2">Componente</th>
                                    <th className="px-3 py-2">En uso</th>
                                    <th className="px-3 py-2">Última</th>
                                    <th className="px-3 py-2">Fin de soporte</th>
                                    <th className="px-3 py-2">Estado</th>
                                    <th className="px-3 py-2" />
                                </tr>
                            </thead>
                            <tbody>
                                {visible
                                    .filter((c) => c.category === cat)
                                    .sort((a, b) => SEVERITY.indexOf(a.status) - SEVERITY.indexOf(b.status))
                                    .map((c) => (
                                        <tr key={c.id} className="border-t border-slate-800 align-top">
                                            <td className="px-3 py-3">
                                                <div className="font-medium text-slate-100">
                                                    {c.name}
                                                    {c.shared && (
                                                        <span className="ml-2 rounded bg-sky-500/15 px-1.5 py-0.5 text-[10px] uppercase text-sky-300" title="Compartido con las otras apps del servidor">
                                                            compartido
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="mt-0.5 max-w-xs text-xs text-slate-500">{c.role}</div>
                                            </td>
                                            <td className="px-3 py-3 font-mono text-slate-200" title={c.currentSource ?? undefined}>
                                                {c.currentVersion ?? '—'}
                                            </td>
                                            <td className="px-3 py-3 font-mono text-slate-300">{c.latestVersion ?? '—'}</td>
                                            <td className="whitespace-nowrap px-3 py-3 text-slate-300">{fmtDate(c.eolDate)}</td>
                                            <td className="px-3 py-3">
                                                <StatusBadge status={c.status} />
                                                <div className="mt-1 max-w-sm text-xs text-slate-400">{c.detail}</div>
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-3 text-right">
                                                {c.procedure && (
                                                    <button onClick={() => onProcedure(c.procedure)} className="rounded-md border border-slate-700 px-2 py-1 text-xs text-slate-300 hover:bg-slate-800">
                                                        Cómo actualizar
                                                    </button>
                                                )}
                                                {c.docs && (
                                                    <a href={c.docs} target="_blank" rel="noopener noreferrer" className="ml-2 text-xs text-indigo-400 underline">
                                                        Oficial
                                                    </a>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            ))}
        </div>
    )
}

function RoadmapTab({ items, components, showClosed, onToggleClosed, onChanged }: {
    items: RoadmapItem[]
    components: MaintenanceComponent[]
    showClosed: boolean
    onToggleClosed: () => void
    onChanged: () => void
}) {
    const [creating, setCreating] = useState(false)
    const [expanded, setExpanded] = useState<number | null>(null)
    const [form, setForm] = useState({ title: '', componentId: '', priority: 'MEDIA' as RoadmapPriority, dueDate: '', description: '' })
    const names = useMemo(() => Object.fromEntries(components.map((c) => [c.id, c.name])), [components])
    const today = new Date().toISOString().slice(0, 10)

    const create = async (e: React.FormEvent) => {
        e.preventDefault()
        try {
            await maintenanceService.createItem({ ...form, componentId: form.componentId || undefined, dueDate: form.dueDate || undefined })
            setForm({ title: '', componentId: '', priority: 'MEDIA', dueDate: '', description: '' })
            setCreating(false)
            toast.success('Tarea agregada')
            onChanged()
        } catch (err) {
            toast.error(errorMessage(err, 'No se pudo agregar la tarea'))
        }
    }

    const update = async (id: number, input: Parameters<typeof maintenanceService.updateItem>[1]) => {
        try {
            await maintenanceService.updateItem(id, input)
            onChanged()
        } catch (err) {
            toast.error(errorMessage(err, 'No se pudo actualizar la tarea'))
        }
    }

    const remove = async (item: RoadmapItem) => {
        if (!window.confirm(`¿Borrar la tarea "${item.title}"?`)) return
        try {
            await maintenanceService.deleteItem(item.id)
            onChanged()
        } catch (err) {
            toast.error(errorMessage(err, 'No se pudo borrar la tarea'))
        }
    }

    return (
        <div>
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-slate-400">
                    Las tareas <span className="text-sky-300">automáticas</span> las crea la revisión mensual y se cierran solas cuando el
                    componente queda al día. Agrega las tuyas para no olvidar nada.
                </p>
                <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 text-sm text-slate-400">
                        <input type="checkbox" checked={showClosed} onChange={onToggleClosed} /> Mostrar cerradas
                    </label>
                    <button onClick={() => setCreating((v) => !v)} className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm text-white hover:bg-indigo-500">
                        {creating ? 'Cancelar' : 'Nueva tarea'}
                    </button>
                </div>
            </div>

            {creating && (
                <form onSubmit={create} className="mb-6 grid grid-cols-1 gap-3 rounded-xl border border-slate-800 bg-slate-800/40 p-4 sm:grid-cols-4">
                    <input aria-label="Título" placeholder="Título (ej. Reiniciar el servidor por kernel pendiente)" required maxLength={200}
                        value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={`${inputCls} sm:col-span-4`} />
                    <select aria-label="Componente" value={form.componentId} onChange={(e) => setForm({ ...form, componentId: e.target.value })} className={inputCls}>
                        <option value="">Sin componente</option>
                        {components.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                    <select aria-label="Prioridad" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value as RoadmapPriority })} className={inputCls}>
                        <option value="ALTA">Prioridad alta</option>
                        <option value="MEDIA">Prioridad media</option>
                        <option value="BAJA">Prioridad baja</option>
                    </select>
                    <input aria-label="Fecha límite" type="date" value={form.dueDate} onChange={(e) => setForm({ ...form, dueDate: e.target.value })} className={inputCls} />
                    <button type="submit" className="rounded-md bg-emerald-600 px-3 py-2 text-sm text-white hover:bg-emerald-500">Agregar</button>
                    <textarea aria-label="Descripción" placeholder="Notas, pasos, enlaces…" rows={3} maxLength={5000}
                        value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={`${inputCls} sm:col-span-4`} />
                </form>
            )}

            {items.length === 0 ? (
                <div className="text-sm text-slate-400">No hay tareas{showClosed ? '' : ' abiertas'}.</div>
            ) : (
                <ul className="space-y-2">
                    {items.map((item) => {
                        const closed = item.status === 'HECHO' || item.status === 'DESCARTADO'
                        const overdue = !closed && item.dueDate !== null && item.dueDate < today
                        return (
                            <li key={item.id} className={`rounded-xl border border-slate-800 bg-slate-800/40 p-3 ${closed ? 'opacity-60' : ''}`}>
                                <div className="flex flex-wrap items-center gap-2">
                                    <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${PRIORITY_BADGE[item.priority]}`}>{item.priority}</span>
                                    {item.source === 'AUTO' && <span className="rounded bg-sky-500/15 px-1.5 py-0.5 text-[10px] uppercase text-sky-300">automática</span>}
                                    <button onClick={() => setExpanded(expanded === item.id ? null : item.id)} className={`text-left font-medium text-slate-100 hover:underline ${closed ? 'line-through' : ''}`}>
                                        {item.title}
                                    </button>
                                    <span className="ml-auto flex items-center gap-2 text-xs text-slate-400">
                                        {item.componentId && <span>{names[item.componentId] ?? item.componentId}</span>}
                                        <span className={overdue ? 'font-semibold text-rose-300' : ''}>
                                            {item.dueDate ? `vence ${fmtDate(item.dueDate)}` : 'sin fecha'}
                                        </span>
                                        <select aria-label={`Estado de ${item.title}`} value={item.status}
                                            onChange={(e) => update(item.id, { status: e.target.value as RoadmapStatus })}
                                            className="rounded border border-slate-600 bg-slate-900 px-2 py-1 text-xs text-slate-100">
                                            {(Object.keys(ROADMAP_STATUS) as RoadmapStatus[]).map((s) => <option key={s} value={s}>{ROADMAP_STATUS[s]}</option>)}
                                        </select>
                                    </span>
                                </div>
                                {expanded === item.id && (
                                    <div className="mt-3 space-y-3 border-t border-slate-800 pt-3 text-sm">
                                        <p className="whitespace-pre-wrap text-slate-300">{item.description || 'Sin descripción.'}</p>
                                        <div className="flex flex-wrap items-center gap-3 text-xs text-slate-400">
                                            <label className="flex items-center gap-2">
                                                Fecha límite
                                                <input type="date" value={item.dueDate ?? ''} onChange={(e) => update(item.id, { dueDate: e.target.value })}
                                                    className="rounded border border-slate-600 bg-slate-900 px-2 py-1 text-slate-100" />
                                            </label>
                                            <label className="flex items-center gap-2">
                                                Prioridad
                                                <select value={item.priority} onChange={(e) => update(item.id, { priority: e.target.value as RoadmapPriority })}
                                                    className="rounded border border-slate-600 bg-slate-900 px-2 py-1 text-slate-100">
                                                    <option value="ALTA">Alta</option>
                                                    <option value="MEDIA">Media</option>
                                                    <option value="BAJA">Baja</option>
                                                </select>
                                            </label>
                                            <span>Creada {fmtDate(item.createdAt)} por {item.createdBy ?? '—'}</span>
                                            {item.source === 'MANUAL' && (
                                                <button onClick={() => remove(item)} className="ml-auto text-rose-300 hover:underline">Borrar</button>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </li>
                        )
                    })}
                </ul>
            )}
        </div>
    )
}

function ProceduresTab({ onOpen }: { onOpen: (id: string) => void }) {
    const [procedures, setProcedures] = useState<Procedure[]>([])
    useEffect(() => {
        maintenanceService.procedures().then(setProcedures).catch(() => toast.error('No se pudieron cargar los procedimientos'))
    }, [])
    return (
        <div>
            <p className="mb-4 text-sm text-slate-400">Pasos probados para actualizar cada parte del sistema, con los archivos que hay que tocar.</p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {procedures.map((p) => (
                    <button key={p.id} onClick={() => onOpen(p.id)} className="rounded-xl border border-slate-800 bg-slate-800/50 p-4 text-left text-slate-100 hover:bg-slate-800">
                        {p.title}
                    </button>
                ))}
            </div>
        </div>
    )
}
